import { ReactNode, useEffect } from "react";
import { AlertTriangle, CalendarClock, Check, CheckCheck, Clock, Eye, MousePointerClick, Send, X, XCircle } from "lucide-react";
import { Badge } from "components/ui/Badge";
import { Button } from "components/ui/Button";
import { EmailStats, EmailingUser, MailCategory, MailSend, MailSendStatus, UserSegment, resendTemplateUrl } from "interfaces/emailing";

/** Every date of the page is French time, wherever the admin is. */
export const PARIS = "Europe/Paris";

export function formatDate(d?: string, withTime = false) {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("fr-FR", { timeZone: PARIS, day: "numeric", month: "short", year: "numeric", ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}) });
}

/** `"2026-10-28T09:00"` for a date, in Paris time — the shape the date/time inputs and the backend use. */
export function parisInputValue(d: Date) {
    const p = Object.fromEntries(
        new Intl.DateTimeFormat("en-GB", { timeZone: PARIS, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
            .formatToParts(d)
            .map((x) => [x.type, x.value])
    );
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** A programmed send whose time has passed went out, even if no webhook said so. */
export const effectiveStatus = (s: Pick<MailSend, "status" | "scheduledFor">): MailSendStatus =>
    s.status === "scheduled" && s.scheduledFor && new Date(s.scheduledFor).getTime() <= Date.now() ? "sent" : s.status;

export function since(d?: string) {
    if (!d) return "jamais";
    const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
    if (days <= 0) return "aujourd'hui";
    if (days === 1) return "hier";
    if (days < 30) return `il y a ${days} j`;
    if (days < 365) return `il y a ${Math.floor(days / 30)} mois`;
    return `il y a ${Math.floor(days / 365)} an${days >= 730 ? "s" : ""}`;
}

export const SEGMENTS: { id: UserSegment; label: string; hint: string }[] = [
    { id: "all", label: "Tous", hint: "Tous les comptes" },
    { id: "customers", label: "Clients premium", hint: "Abonnés" },
    { id: "trial", label: "En essai", hint: "Essai en cours" },
    { id: "trial_ending", label: "Essai finit sous 7 j", hint: "À convertir maintenant" },
    { id: "trial_expired", label: "Essai terminé", hint: "Prospects à relancer" },
    { id: "inactive", label: "Inactifs 30 j", hint: "Pas connectés depuis 30 jours" },
    { id: "never_emailed", label: "Jamais contactés", hint: "Aucun email envoyé d'ici" },
    { id: "opted_out", label: "Désinscrits", hint: "Ne reçoivent plus le marketing" },
];

const STATUS: Record<MailSendStatus, { label: string; tone: "neutral" | "success" | "warning" | "danger" | "info"; icon: typeof Check }> = {
    queued: { label: "En file", tone: "neutral", icon: Clock },
    scheduled: { label: "Programmé", tone: "info", icon: CalendarClock },
    canceled: { label: "Annulé", tone: "neutral", icon: X },
    sent: { label: "Envoyé", tone: "neutral", icon: Check },
    delayed: { label: "Retardé", tone: "warning", icon: Clock },
    delivered: { label: "Délivré", tone: "info", icon: CheckCheck },
    opened: { label: "Ouvert", tone: "success", icon: Eye },
    clicked: { label: "Cliqué", tone: "success", icon: MousePointerClick },
    bounced: { label: "Rebond", tone: "danger", icon: XCircle },
    complained: { label: "Signalé spam", tone: "danger", icon: AlertTriangle },
    failed: { label: "Échec", tone: "danger", icon: XCircle },
};

export const statusLabel = (s: string) => STATUS[s as MailSendStatus]?.label ?? s;

export function StatusBadge({ status }: { status: MailSendStatus }) {
    const s = STATUS[status] ?? STATUS.sent;
    const Icon = s.icon;
    return (
        <Badge tone={s.tone} className="whitespace-nowrap px-2">
            <Icon className="h-3 w-3" />
            {s.label}
        </Badge>
    );
}

export function CategoryBadge({ category }: { category: MailCategory }) {
    return <Badge className="whitespace-nowrap px-2">{category === "marketing" ? "Marketing" : "Transactionnel"}</Badge>;
}

export function OfferBadge({ user }: { user: Pick<EmailingUser, "offer" | "trialEndsAt"> }) {
    if (user.offer === "Premium") return <Badge tone="success" className="whitespace-nowrap px-2">Premium</Badge>;
    if (user.offer === "Essai") {
        const days = user.trialEndsAt ? Math.ceil((new Date(user.trialEndsAt).getTime() - Date.now()) / 86400000) : null;
        return (
            <Badge tone={days !== null && days <= 7 ? "warning" : "neutral"} className="whitespace-nowrap px-2">
                Essai{days !== null ? ` · ${days} j` : ""}
            </Badge>
        );
    }
    if (user.offer === "Essai terminé") return <Badge className="whitespace-nowrap px-2">Essai terminé</Badge>;
    return <Badge className="whitespace-nowrap px-2">Sans abonnement</Badge>;
}

/** Resend sends the published version, but its API only gives us the latest one — they may differ. */
export function UnpublishedNotice({ id }: { id: string }) {
    return (
        <div className="flex items-start gap-2 rounded-lg border border-warning/20 bg-warning-subtle p-3 text-xs text-ink">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <span>
                Ce modèle a des modifications non publiées dans Resend : l'aperçu et l'envoi peuvent reprendre ce brouillon.{" "}
                <a href={resendTemplateUrl(id)} target="_blank" rel="noreferrer" className="underline">
                    Publiez-le
                </a>{" "}
                avant d'envoyer : la page reprend les modèles Resend toutes les minutes.
            </span>
        </div>
    );
}

/** Centered dialog. `wide` for the composer and the template editor. */
export function Modal({ title, subtitle, onClose, children, footer, wide }: { title: ReactNode; subtitle?: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div role="dialog" aria-modal="true" className={`flex h-[92vh] w-full flex-col rounded-2xl border border-border bg-surface shadow-float ${wide ? "max-w-7xl" : "max-w-3xl"}`}>
                <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
                    <div className="min-w-0">
                        <h2 className="truncate font-sans text-base font-semibold text-ink">{title}</h2>
                        {subtitle && <p className="mt-0.5 text-sm text-ink-muted">{subtitle}</p>}
                    </div>
                    <button onClick={onClose} aria-label="Fermer" className="rounded-lg p-1.5 text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
                {footer && <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-6 py-4">{footer}</div>}
            </div>
        </div>
    );
}

/**
 * The real email in an iframe — the exact HTML Resend gets. `flex-1 min-h-0` from the parent, never a
 * percentage height: under a parent free to shrink, an iframe collapses to its default 150px.
 */
export function MailFrame({ html, subject, from, mobile, loading }: { html?: string; subject?: string; from?: string; mobile?: boolean; loading?: boolean }) {
    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-surface-sunken">
            <div className="space-y-0.5 border-b border-border bg-surface px-4 py-3">
                {from && <p className="truncate text-xs text-ink-faint">De : {from}</p>}
                <p className="truncate text-sm font-semibold text-ink">{subject || <span className="text-ink-faint">Sans objet</span>}</p>
            </div>
            <div className="flex min-h-0 flex-1 justify-center overflow-hidden">
                {html ? (
                    <iframe
                        title="Aperçu de l'email"
                        srcDoc={html}
                        sandbox=""
                        className={`h-full border-0 bg-canvas transition-all duration-150 ${mobile ? "w-96 border-x border-border" : "w-full"} ${loading ? "opacity-60" : ""}`}
                    />
                ) : (
                    <p className="self-center text-sm text-ink-faint">{loading ? "Chargement de l'aperçu…" : "Aucun aperçu"}</p>
                )}
            </div>
        </div>
    );
}

export function Segmented<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode }[] }) {
    return (
        <div className="inline-flex rounded-full bg-surface-sunken p-1">
            {options.map((o) => (
                <button
                    key={o.value}
                    type="button"
                    onClick={() => onChange(o.value)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                        value === o.value ? "bg-surface text-ink shadow-card" : "text-ink-muted hover:text-ink"
                    }`}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

export const fieldLabel = "mb-1.5 block text-sm font-medium text-ink";
export const textareaClass =
    "w-full rounded-lg border border-border bg-surface px-3 py-2 font-sans text-sm text-ink placeholder:text-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent";

/** « Dernier email envoyé » cell: which template, and when. */
export function LastEmailCell({ emails }: { emails: EmailStats }) {
    if (!emails.lastSentAt) return <span className="text-ink-faint">Aucun</span>;
    return (
        <span className="block min-w-0">
            <span className="block truncate text-ink">{emails.lastTemplate || emails.lastSubject}</span>
            <span className="block text-xs text-ink-muted">{formatDate(emails.lastSentAt, true)}</span>
        </span>
    );
}

export function Pager({ page, totalPages, setPage }: { page: number; totalPages: number; setPage: (p: number) => void }) {
    if (totalPages <= 1) return null;
    return (
        <div className="flex items-center justify-end gap-2 text-sm text-ink-muted">
            <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Précédent
            </Button>
            Page {page} / {totalPages}
            <Button size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                Suivant
            </Button>
        </div>
    );
}

/** Floating bar shown while rows are ticked: count, clear, and the one action. */
export function SelectionBar({ count, note, onClear, onSend }: { count: number; note?: ReactNode; onClear: () => void; onSend: () => void }) {
    if (!count) return null;
    return (
        <div className="fixed inset-x-0 bottom-6 z-30 flex justify-center px-4">
            <div className="flex items-center gap-4 rounded-2xl bg-ink px-4 py-3 text-sm text-ink-inverse shadow-float">
                <span>
                    {count} sélectionné{count > 1 ? "s" : ""}
                    {note && <span className="text-ink-inverse-muted"> {note}</span>}
                </span>
                <button onClick={onClear} className="text-ink-inverse-muted underline hover:text-ink-inverse">
                    Vider
                </button>
                <Button variant="accent" size="sm" onClick={onSend}>
                    <Send className="h-4 w-4" />
                    Envoyer un email
                </Button>
            </div>
        </div>
    );
}
